import { forwardRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ShoppingCart, Plus, Star } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { ShopifyProduct, CartItem } from "@/lib/shopify";
import { getProductTitle, getProductDescriptionPreview } from "@/lib/productTranslations";
import { useCartStore } from "@/stores/cartStore";
import { useLanguageStore, isRTL } from "@/stores/languageStore";
import { toast } from "sonner";

// Original prices mapping for hot deal products (sale price -> original price)
const ORIGINAL_PRICES: Record<number, number> = {
  70: 200,   // Endoscope camera
  90: 180,   // Laser level
  120: 220,  // Laser level
  180: 300,  // Laser level
  720: 850,  // Smart nightstand
};

interface HotDealProductCardProps {
  product: ShopifyProduct;
}

export const HotDealProductCard = forwardRef<HTMLAnchorElement, HotDealProductCardProps>(
  ({ product }, ref) => {
    const addItem = useCartStore((state) => state.addItem);
    const { t, language } = useLanguageStore();
    const rtl = isRTL(language);
    const iconSpacing = rtl ? "ml-1" : "mr-1";
    const navigate = useNavigate();
    const { node } = product;

    const image = node.images.edges[0]?.node;
    const firstVariant = node.variants.edges[0]?.node;

    const hasOnlyDefaultTitleOption =
      node.options?.length === 1 &&
      node.options[0]?.name === "Title" &&
      node.options[0]?.values?.length === 1 &&
      node.options[0]?.values?.[0] === "Default Title";

    const hasMultipleVariants = node.variants.edges.length > 1;
    const requiresOptionSelection = hasMultipleVariants && !hasOnlyDefaultTitleOption;

    const price = node.priceRange.minVariantPrice;
    const salePrice = parseFloat(price.amount);

    // Hot-deal strike-through mapping is only safe for single-variant items
    const originalPrice = !requiresOptionSelection ? (ORIGINAL_PRICES[salePrice] || null) : null;

    const handleAddToCart = (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();

      if (!firstVariant) return;

      if (requiresOptionSelection) {
        toast.info(t("products.selectOptionsToast"), {
          position: "top-center",
        });
        navigate(`/product/${node.handle}`);
        return;
      }

      const cartItem: CartItem = {
        product,
        variantId: firstVariant.id,
        variantTitle: firstVariant.title,
        price: firstVariant.price,
        quantity: 1,
        selectedOptions: firstVariant.selectedOptions || [],
      };

      addItem(cartItem);
      toast.success(t("cart.added"), {
        description: getProductTitle(node, language),
        position: "top-center",
      });

      window.dispatchEvent(new CustomEvent("cart:open"));
    };

    const handleReviewClick = (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      navigate(`/product/${node.handle}#reviews`);
    };

    // Calculate discount percentage
    const discountPercent = originalPrice 
      ? Math.round(((originalPrice - salePrice) / originalPrice) * 100)
      : 0;

    return (
      <Link to={`/product/${node.handle}`} ref={ref}>
        <Card className="group overflow-hidden border-border/50 bg-card transition-all duration-300 hover:shadow-luxury hover:border-primary/20">
          <div className="relative aspect-square overflow-hidden bg-secondary/30">
            {image ? (
              <img
                src={image.url}
                alt={image.altText || node.title}
                className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                loading="lazy"
              />
            ) : (
              <div className="flex h-full w-full items-center justify-center bg-secondary">
                <ShoppingCart className="h-12 w-12 text-muted-foreground" />
              </div>
            )}

            {/* Discount badge */}
            {discountPercent > 0 && (
              <div className="absolute top-3 left-3 bg-red-600 text-white px-2 py-1 rounded-md text-sm font-bold">
                -{discountPercent}%
              </div>
            )}

            <Button
              size="icon"
              className="absolute bottom-3 right-3 opacity-0 translate-y-2 transition-all duration-300 group-hover:opacity-100 group-hover:translate-y-0 bg-primary hover:bg-primary/90 shadow-elegant"
              onClick={handleAddToCart}
              aria-label={requiresOptionSelection ? t("products.selectOptions") : t("products.addToCart")}
            >
              <Plus className="h-4 w-4" />
            </Button>
          </div>

          <CardContent className="p-4">
            <h3 className="font-display text-lg font-semibold line-clamp-1 transition-colors group-hover:text-primary">
              {getProductTitle(node, language)}
            </h3>

            {node.description && (
              <p className="mt-1 text-sm text-muted-foreground line-clamp-2">{getProductDescriptionPreview(node, language)}</p>
            )}

            <div className="mt-3 flex items-center justify-between">
              <div className="flex flex-col">
                <div className="flex items-center gap-2">
                  <span className="text-xl font-bold text-green-500">
                    {requiresOptionSelection ? t("products.startingFrom") : ""}₪{salePrice.toFixed(2)}
                  </span>
                  {originalPrice && (
                    <span className="text-base text-muted-foreground line-through decoration-red-500 decoration-2">
                      ₪{originalPrice.toFixed(2)}
                    </span>
                  )}
                </div>
                {originalPrice && (
                  <span className="text-xs text-green-500 font-medium">
                    {t("products.savings")} ₪{(originalPrice - salePrice).toFixed(2)}!
                  </span>
                )}
              </div>

              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  className="text-xs border-primary/30 hover:bg-primary hover:text-primary-foreground"
                  onClick={handleAddToCart}
                >
                  <ShoppingCart className={`h-3 w-3 ${iconSpacing}`} />
                  {requiresOptionSelection ? t("products.selectOptions") : t("products.addToCart")}
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      </Link>
    );
  }
);

HotDealProductCard.displayName = "HotDealProductCard";
